import { Component, OnInit, HostListener, ElementRef, TemplateRef, ViewChild, AfterViewInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { HeaderComponent } from '../../header/header.component';
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { ProviderSidebarComponent } from '../../../../provider/sidebar/provider-sidebar.component';
import { OrgAdminSidebarComponent } from '../../../../org-admin/sidebar/org-admin-sidebar.component';
import { environment } from '../../../../../environments/environment';
import { YearRangePickerComponent } from '../../primeng/year-range-picker/year-range-picker.component';
import { StepperComponent, StepperStep } from '../../primeng';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { getDropdownShouldOpenUpward } from '../../../config/dropdown.config';

export interface DatasetData {
  title: string;
  permission: 'Open' | 'Restricted';
}

@Component({
  selector: 'app-provider-metadata',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    HeaderComponent,
    ProviderSidebarComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    YearRangePickerComponent,
    StepperComponent,
  ],
  templateUrl: './metadata.component.html',
  styleUrl: './metadata.component.scss',
})
export class ProviderMetadataComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();

  get isOrgAdminRoute(): boolean {
    return this.router.url.includes('/org-admin/');
  }

  metadataForm!: FormGroup;
  currentStep = 1; // Step 2 (0-based index) - Enter Metadata
  totalSteps = 4;
  
  @ViewChild('step2Template', { static: true }) step2Template!: TemplateRef<any>;

  stepperSteps: StepperStep[] = [
    {
      key: 'step1',
      label: 'Setup your datasets',
    },
    {
      key: 'step2',
      label: 'Enter Metadata',
    },
    {
      key: 'step3',
      label: 'Additional Info',
    },
    {
      key: 'step4',
      label: 'Upload files',
    },
  ];

  /** Step 1 label from route: "Setup your Models" on add-model, "Setup your datasets" on add-dataset */
  get stepperStepsWithLabel(): StepperStep[] {
    const label = this.router.url.includes('add-model') ? 'Setup your Models' : 'Setup your datasets';
    return this.stepperSteps.map((step, i) => i === 0 ? { ...step, label } : step);
  }

  datasetData: DatasetData | null = null;
  isLicenseDropdownOpen = false;
  isResourceServerDropdownOpen = false;
  isUploadFrequencyDropdownOpen = false;
  isIndustryDropdownOpen = false;
  
  // Track if dropdowns should open upward
  isLicenseDropdownUpward = false;
  isResourceServerDropdownUpward = false;
  isUploadFrequencyDropdownUpward = false;
  isIndustryDropdownUpward = false;

  licenseOptions: Array<{ value: string; label: string }> = [
    { value: 'Open Government License, India', label: 'Open Government License, India' },
    { value: 'CC-BY 4.0', label: 'CC-BY 4.0' },
    { value: 'Other', label: 'Other' }
  ];

  industryOptions: Array<{ value: string; label: string }> = [
    { value: 'Agriculture', label: 'Agriculture' },
    { value: 'forest', label: 'Forest' },
    { value: 'Water', label: 'Water' },
    { value: 'Energy', label: 'Energy' },
    { value: 'Transport', label: 'Transport' },
    { value: 'Health', label: 'Health' },
    { value: 'Education', label: 'Education' },
    { value: 'Other', label: 'Other' }
  ];

  resourceServerOptions: Array<{ value: string; label: string; displayLabel: string; name: string; url: string }> = [];

  uploadFrequencyOptions: Array<{ value: string; label: string }> = [
    { value: 'OneTime', label: 'OneTime' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'Quarterly', label: 'Quarterly' },
    { value: 'semi-annually', label: 'Semi-Annually' },
    { value: 'Annually', label: 'Annually' }
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  removeProtocol(url: string): string {
    return url.replace(/^https?:\/\//, '');
  }

  private initializeResourceServerOptions(): void {
    try {
      // Initialize resource server options from environment
      const env = environment as any;
      
      const resourceServers = [
        { name: 'OGC', url: env.ogcApiPath || '' },
        { name: 'NGSI-LD', url: env.ngsiLdApiPath || '' },
        { name: 'GATEWAY', url: env.gatewayApiPath || '' }
      ];
      
      this.resourceServerOptions = resourceServers
        .filter(server => server.url && server.url.trim() !== '') // Only include servers with valid URLs
        .map((server: { name: string; url: string }) => ({
          value: server.url,
          name: server.name,
          url: this.removeProtocol(server.url),
          label: `${server.name} (${this.removeProtocol(server.url)})`,
          displayLabel: `${server.name} (${this.removeProtocol(server.url)})`
        }));
      
      // Fallback: If no options loaded, use hardcoded values
      if (this.resourceServerOptions.length === 0) {
        this.resourceServerOptions = [
          { value: 'http://v2.dev.ogc.iudx.io/', name: 'OGC', url: 'v2.dev.ogc.iudx.io/', label: 'OGC (v2.dev.ogc.iudx.io/)', displayLabel: 'OGC (v2.dev.ogc.iudx.io/)' },
          { value: 'https://v2.dev.rs.iudx.io/', name: 'NGSI-LD', url: 'v2.dev.rs.iudx.io/', label: 'NGSI-LD (v2.dev.rs.iudx.io/)', displayLabel: 'NGSI-LD (v2.dev.rs.iudx.io/)' },
          { value: 'https://v2.dev.rs-proxy.iudx.io/', name: 'GATEWAY', url: 'v2.dev.rs-proxy.iudx.io/', label: 'GATEWAY (v2.dev.rs-proxy.iudx.io/)', displayLabel: 'GATEWAY (v2.dev.rs-proxy.iudx.io/)' }
        ];
      }
    } catch (error) {
      // Fallback to hardcoded values on error
      this.resourceServerOptions = [
        { value: 'http://v2.dev.ogc.iudx.io/', name: 'OGC', url: 'v2.dev.ogc.iudx.io/', label: 'OGC (v2.dev.ogc.iudx.io/)', displayLabel: 'OGC (v2.dev.ogc.iudx.io/)' },
        { value: 'https://v2.dev.rs.iudx.io/', name: 'NGSI-LD', url: 'v2.dev.rs.iudx.io/', label: 'NGSI-LD (v2.dev.rs.iudx.io/)', displayLabel: 'NGSI-LD (v2.dev.rs.iudx.io/)' },
        { value: 'https://v2.dev.rs-proxy.iudx.io/', name: 'GATEWAY', url: 'v2.dev.rs-proxy.iudx.io/', label: 'GATEWAY (v2.dev.rs-proxy.iudx.io/)', displayLabel: 'GATEWAY (v2.dev.rs-proxy.iudx.io/)' }
      ];
    }
  }

  /**
   * Get the localStorage key based on current route
   */
  private getLocalStorageKey(): string {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/add-model/')) {
      return 'modelFormData';
    }
    return 'datasetFormData'; // Default to dataset
  }

  /**
   * Check if current route is for model creation
   */
  isModel(): boolean {
    return this.router.url.includes('/add-model/');
  }

  ngOnInit(): void {
    // Initialize resource server options from environment FIRST
    this.initializeResourceServerOptions();

    const localStorageKey = this.getLocalStorageKey();
    const navigationStepData = this.getNavigationStepData();
    if (navigationStepData) {
      this.datasetData = {
        title: navigationStepData.title ?? '',
        permission: navigationStepData.permission ?? 'Open',
      };
      this.mergeStepDataIntoLocalStorage(navigationStepData as Record<string, unknown>);
    } else {
      this.loadFlowStateFromStorage();
    }

    // Restore metadata form data from localStorage if available
    const savedData = localStorage.getItem(localStorageKey);
    let savedMetadataData: any = {};
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        savedMetadataData = {
          shortDescription: parsedData.shortDescription || '',
          longDescription: parsedData.longDescription || '',
          license: parsedData.license || '',
          otherLicense: parsedData.otherLicense || '',
          resourceServer: parsedData.resourceServer || '',
          uploadFrequency: parsedData.uploadFrequency || '',
          yearRange: parsedData.yearRange || null,
          geoCoverage: parsedData.geoCoverage || '',
          industry: parsedData.industry || ''
        };
      } catch (error) {
        console.error('Error parsing saved metadata data:', error);
      }
    }

    // Initialize form based on whether it's a model or dataset
    if (this.isModel()) {
      // For models: Short/Long Description (min 50 chars) + Industry and License
      this.metadataForm = this.fb.group({
        shortDescription: [savedMetadataData.shortDescription || '', [Validators.required, Validators.minLength(50), Validators.maxLength(200)]],
        longDescription: [savedMetadataData.longDescription || '', [Validators.required, Validators.minLength(50), Validators.maxLength(2000)]],
        industry: [savedMetadataData.industry || '', [Validators.required]],
        license: [savedMetadataData.license || '', [Validators.required]],
        otherLicense: [savedMetadataData.otherLicense || '']
      });
    } else {
      // For datasets: all fields
      this.metadataForm = this.fb.group({
        shortDescription: [savedMetadataData.shortDescription || '', [Validators.required, Validators.minLength(50), Validators.maxLength(200)]],
        longDescription: [savedMetadataData.longDescription || '', [Validators.required, Validators.minLength(50), Validators.maxLength(2000)]],
        license: [savedMetadataData.license || '', [Validators.required]],
        otherLicense: [savedMetadataData.otherLicense || ''],
        resourceServer: [savedMetadataData.resourceServer || '', [Validators.required]],
        uploadFrequency: [savedMetadataData.uploadFrequency || '', [Validators.required]],
        yearRange: [savedMetadataData.yearRange || null, [Validators.required]],
        geoCoverage: [savedMetadataData.geoCoverage || '', [Validators.required]]
      });
    }

    // Mark form controls as touched if they have values (for proper validation state)
    Object.keys(savedMetadataData).forEach(key => {
      if (savedMetadataData[key] && this.metadataForm.get(key)) {
        this.metadataForm.get(key)?.markAsTouched();
      }
    });

    // Watch for license changes to add/remove validation for otherLicense
    this.metadataForm.get('license')?.valueChanges.subscribe(value => {
      const otherLicenseControl = this.metadataForm.get('otherLicense');
      if (value === 'Other') {
        otherLicenseControl?.setValidators([Validators.required]);
      } else {
        otherLicenseControl?.clearValidators();
        otherLicenseControl?.setValue('');
      }
      otherLicenseControl?.updateValueAndValidity();
    });

    if (this.metadataForm.get('license')?.value === 'Other') {
      const otherLicenseControl = this.metadataForm.get('otherLicense');
      otherLicenseControl?.setValidators([Validators.required]);
      otherLicenseControl?.updateValueAndValidity({ emitEvent: false });
    }

    // Auto-save form data on changes (debounced)
    this.metadataForm.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.saveMetadataFormData();
      });
  }

  ngAfterViewInit(): void {
    // Update step 2 with template reference after view is initialized
    if (this.step2Template) {
      this.stepperSteps[1].content = this.step2Template;
      this.cdr.detectChanges();
    }
  }

  onStepChange(stepIndex: number): void {
    // Save form data before navigating
    this.saveMetadataFormData();
    
    // Determine route base from current URL
    const currentUrl = this.router.url;
    let routeBase: string;
    
    if (currentUrl.includes('/add-dataset/')) {
      routeBase = 'provider/add-contributions/add-dataset';
    } else if (currentUrl.includes('/add-model/')) {
      routeBase = 'provider/add-contributions/add-model';
    } else {
      // Default to add-dataset if we can't determine from URL
      routeBase = 'provider/add-contributions/add-dataset';
    }
    
    // Navigate to the appropriate route based on step index
    const routes = [
      routeBase,
      `${routeBase}/metadata`,
      `${routeBase}/additionalInfo`,
      `${routeBase}/uploads`
    ];
    
    if (stepIndex >= 0 && stepIndex < routes.length) {
      this.router.navigate([routes[stepIndex]]);
    }
  }

  isMetadataFormValid(): boolean {
    // Check if all required fields are filled
    return this.metadataForm.valid;
  }

  onNext(): void {
    // Navigate to next step (additionalInfo)
    if (this.metadataForm.valid) {
      this.proceed();
    }
  }

  onPrevious(): void {
    // Navigate to previous step (dataset)
    this.goBack();
  }

  isOtherLicenseSelected(): boolean {
    return this.metadataForm.get('license')?.value === 'Other';
  }

  toggleLicenseDropdown(): void {
    if (!this.isLicenseDropdownOpen) {
      this.isResourceServerDropdownOpen = false;
      this.isUploadFrequencyDropdownOpen = false;
      this.isIndustryDropdownOpen = false;
      this.checkDropdownPosition('license');
    }
    this.isLicenseDropdownOpen = !this.isLicenseDropdownOpen;
  }
  
  /**
   * Check if dropdown should open upward based on available space
   */
  private checkDropdownPosition(dropdownType: 'license' | 'resourceServer' | 'uploadFrequency' | 'industry'): void {
    setTimeout(() => {
      // Find all dropdown wrappers
      const allWrappers = this.elementRef.nativeElement.querySelectorAll('.custom-dropdown-wrapper');
      let targetWrapper: HTMLElement | null = null;
      
      // Find the specific dropdown wrapper by label text
      if (dropdownType === 'industry') {
        targetWrapper = Array.from(allWrappers).find(w => {
          const label = (w as HTMLElement).closest('.form-field')?.querySelector('label');
          return label?.textContent?.includes('Industry');
        }) as HTMLElement;
      } else if (dropdownType === 'license') {
        targetWrapper = Array.from(allWrappers).find(w => {
          const label = (w as HTMLElement).closest('.form-field')?.querySelector('label');
          return label?.textContent?.includes('License');
        }) as HTMLElement;
      } else if (dropdownType === 'resourceServer') {
        targetWrapper = Array.from(allWrappers).find(w => {
          const label = (w as HTMLElement).closest('.form-field')?.querySelector('label');
          return label?.textContent?.includes('Resource Server');
        }) as HTMLElement;
      } else if (dropdownType === 'uploadFrequency') {
        targetWrapper = Array.from(allWrappers).find(w => {
          const label = (w as HTMLElement).closest('.form-field')?.querySelector('label');
          return label?.textContent?.includes('Upload Frequency');
        }) as HTMLElement;
      }
      
      if (!targetWrapper) return;
      
      const trigger = targetWrapper.querySelector('.custom-dropdown-trigger') as HTMLElement;
      if (!trigger) return;
      
      const rect = trigger.getBoundingClientRect();
      let optionCount = 0;
      if (dropdownType === 'industry') {
        optionCount = this.industryOptions.length;
      } else if (dropdownType === 'license') {
        optionCount = this.licenseOptions.length;
      } else if (dropdownType === 'resourceServer') {
        optionCount = this.resourceServerOptions.length;
      } else if (dropdownType === 'uploadFrequency') {
        optionCount = this.uploadFrequencyOptions.length;
      }
      const shouldOpenUpward = getDropdownShouldOpenUpward(rect, optionCount);
      
      switch (dropdownType) {
        case 'industry':
          this.isIndustryDropdownUpward = shouldOpenUpward;
          break;
        case 'license':
          this.isLicenseDropdownUpward = shouldOpenUpward;
          break;
        case 'resourceServer':
          this.isResourceServerDropdownUpward = shouldOpenUpward;
          break;
        case 'uploadFrequency':
          this.isUploadFrequencyDropdownUpward = shouldOpenUpward;
          break;
      }
      
      this.cdr.detectChanges();
    }, 0);
  }

  selectLicense(option: { value: string; label: string }): void {
    this.metadataForm.patchValue({ license: option.value });
    this.isLicenseDropdownOpen = false;
    this.metadataForm.get('license')?.markAsTouched();
  }

  getSelectedLicenseLabel(): string {
    const selectedValue = this.metadataForm.get('license')?.value;
    if (!selectedValue) {
      return 'Select license';
    }
    const selectedOption = this.licenseOptions.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.label : 'Select license';
  }

  isLicenseSelected(value: string): boolean {
    return this.metadataForm.get('license')?.value === value;
  }

  clearLicense(event: Event): void {
    event.stopPropagation();
    this.metadataForm.patchValue({ license: '' });
    this.metadataForm.get('license')?.markAsTouched();
  }

  // Industry dropdown methods
  toggleIndustryDropdown(): void {
    if (!this.isIndustryDropdownOpen) {
      this.isLicenseDropdownOpen = false;
      this.isResourceServerDropdownOpen = false;
      this.isUploadFrequencyDropdownOpen = false;
      this.checkDropdownPosition('industry');
    }
    this.isIndustryDropdownOpen = !this.isIndustryDropdownOpen;
  }

  selectIndustry(option: { value: string; label: string }): void {
    this.metadataForm.patchValue({ industry: option.value });
    this.isIndustryDropdownOpen = false;
    this.metadataForm.get('industry')?.markAsTouched();
  }

  getSelectedIndustryLabel(): string {
    const selectedValue = this.metadataForm.get('industry')?.value;
    if (!selectedValue) {
      return 'Select industry';
    }
    const option = this.industryOptions.find(opt => opt.value === selectedValue);
    return option ? option.label : selectedValue;
  }

  isIndustrySelected(value: string): boolean {
    return this.metadataForm.get('industry')?.value === value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target?.closest?.('.custom-dropdown-wrapper')) return;
    this.isLicenseDropdownOpen = false;
    this.isResourceServerDropdownOpen = false;
    this.isUploadFrequencyDropdownOpen = false;
    this.isIndustryDropdownOpen = false;
  }

  toggleResourceServerDropdown(): void {
    if (!this.isResourceServerDropdownOpen) {
      this.isLicenseDropdownOpen = false;
      this.isUploadFrequencyDropdownOpen = false;
      this.isIndustryDropdownOpen = false;
      this.checkDropdownPosition('resourceServer');
    }
    this.isResourceServerDropdownOpen = !this.isResourceServerDropdownOpen;
  }

  selectResourceServer(option: { value: string; label: string; displayLabel: string }): void {
    this.metadataForm.patchValue({ resourceServer: option.value });
    this.isResourceServerDropdownOpen = false;
    this.metadataForm.get('resourceServer')?.markAsTouched();
  }

  getSelectedResourceServerLabel(): string {
    const selectedValue = this.metadataForm.get('resourceServer')?.value;
    if (!selectedValue) {
      return 'Select resource server';
    }
    const selectedOption = this.resourceServerOptions.find(opt => opt.value === selectedValue);
    if (selectedOption) {
      return `${selectedOption.name} - ${selectedOption.url}`;
    }
    return 'Select resource server';
  }

  getSelectedResourceServerName(): string {
    const selectedValue = this.metadataForm.get('resourceServer')?.value;
    if (!selectedValue) {
      return '';
    }
    const selectedOption = this.resourceServerOptions.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.name : '';
  }

  getSelectedResourceServerUrl(): string {
    const selectedValue = this.metadataForm.get('resourceServer')?.value;
    if (!selectedValue) {
      return '';
    }
    const selectedOption = this.resourceServerOptions.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.url : '';
  }

  isResourceServerSelected(value: string): boolean {
    return this.metadataForm.get('resourceServer')?.value === value;
  }

  clearResourceServer(event: Event): void {
    event.stopPropagation();
    this.metadataForm.patchValue({ resourceServer: '' });
    this.metadataForm.get('resourceServer')?.markAsTouched();
  }

  toggleUploadFrequencyDropdown(): void {
    if (!this.isUploadFrequencyDropdownOpen) {
      this.isLicenseDropdownOpen = false;
      this.isResourceServerDropdownOpen = false;
      this.isIndustryDropdownOpen = false;
      this.checkDropdownPosition('uploadFrequency');
    }
    this.isUploadFrequencyDropdownOpen = !this.isUploadFrequencyDropdownOpen;
  }

  selectUploadFrequency(option: { value: string; label: string }): void {
    this.metadataForm.patchValue({ uploadFrequency: option.value });
    this.isUploadFrequencyDropdownOpen = false;
    this.metadataForm.get('uploadFrequency')?.markAsTouched();
  }

  getSelectedUploadFrequencyLabel(): string {
    const selectedValue = this.metadataForm.get('uploadFrequency')?.value;
    if (!selectedValue) {
      return 'Select upload frequency';
    }
    const selectedOption = this.uploadFrequencyOptions.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.label : 'Select upload frequency';
  }

  isUploadFrequencySelected(value: string): boolean {
    return this.metadataForm.get('uploadFrequency')?.value === value;
  }

  clearUploadFrequency(event: Event): void {
    event.stopPropagation();
    this.metadataForm.patchValue({ uploadFrequency: '' });
    this.metadataForm.get('uploadFrequency')?.markAsTouched();
  }

  getShortDescriptionControl(): AbstractControl | null {
    return this.metadataForm.get('shortDescription');
  }

  getLongDescriptionControl(): AbstractControl | null {
    return this.metadataForm.get('longDescription');
  }

  getShortDescriptionLength(): number {
    const value = this.getShortDescriptionControl()?.value || '';
    return value.length;
  }

  getLongDescriptionLength(): number {
    const value = this.getLongDescriptionControl()?.value || '';
    return value.length;
  }

  getShortDescriptionMaxLength(): number {
    return 200;
  }

  getLongDescriptionMaxLength(): number {
    return 2000;
  }

  goBack(): void {
    // Save form data before navigating back
    this.saveMetadataFormData();
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  proceed(): void {
    if (this.metadataForm.valid) {
      // Save form data to localStorage
      this.saveMetadataFormData();
      
      // Navigate to additionalInfo component (Step 3)
      this.router.navigate(['../additionalInfo'], { relativeTo: this.route });
    }
  }

  private getNavigationStepData(): Partial<DatasetData> | null {
    const navigation = this.router.getCurrentNavigation();
    const fromNav =
      navigation?.extras?.state?.['contributionData'] ??
      navigation?.extras?.state?.['datasetData'];
    const fromHistory =
      window.history.state?.contributionData ?? window.history.state?.datasetData;
    const data = fromNav ?? fromHistory;
    return data ?? null;
  }

  private loadFlowStateFromStorage(): void {
    const localStorageKey = this.getLocalStorageKey();
    const savedData = localStorage.getItem(localStorageKey);
    if (!savedData) {
      return;
    }
    try {
      const parsed = JSON.parse(savedData) as DatasetData & { metadataOption?: string };
      this.datasetData = {
        title: parsed.title ?? '',
        permission: parsed.permission ?? 'Open',
      };
    } catch (error) {
      console.error('Error parsing saved data:', error);
      localStorage.removeItem(localStorageKey);
    }
  }

  private mergeStepDataIntoLocalStorage(partial: Record<string, unknown>): void {
    const localStorageKey = this.getLocalStorageKey();
    let existing: Record<string, unknown> = {};
    try {
      existing = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
    } catch {
      /* ignore malformed storage */
    }
    localStorage.setItem(localStorageKey, JSON.stringify({ ...existing, ...partial }));
  }

  /**
   * Save metadata form data to localStorage
   */
  private saveMetadataFormData(): void {
    if (!this.metadataForm) {
      return;
    }
    const localStorageKey = this.getLocalStorageKey();
    const existingData = localStorage.getItem(localStorageKey);
    let existingFormData: Record<string, unknown> = {};
    try {
      existingFormData = existingData ? JSON.parse(existingData) : {};
    } catch {
      existingFormData = {};
    }

    const formData = {
      ...existingFormData,
      ...this.datasetData,
      ...this.metadataForm.value,
    };

    localStorage.setItem(localStorageKey, JSON.stringify(formData));
  }

  /** UI only: show "Public" for "Open", API still uses OPEN */
  getPermissionDisplayLabel(permission: string | null | undefined): string {
    return !permission ? '' : permission === 'Open' ? 'Public' : permission;
  }

  ngOnDestroy(): void {
    this.saveMetadataFormData();
    this.destroy$.next();
    this.destroy$.complete();
  }
}

